import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderTextArea } from '../../shared/formComponents';
import CollapsibleSection from './shared/CollapsibleSection';

/**
 * Amazon Keyword Fields Component
 *
 * Renders the "Negative Keywords" and "Custom Keywords" text areas along with
 * their associated match-type checkboxes. Both text areas accept a list of
 * keywords separated by commas; line breaks are converted to a CSV via the
 * shared `itemCsvInput` formatter and stored as an array in budget_json.
 *
 * These values are stored at the ROOT of budget_json (not under an ad-platform
 * node) so the same structure can be reused for other ad platforms later.
 *
 * NOTE: The shared CheckBox component calls onChange(defaultChecked) on mount,
 * which would otherwise reset redux-form's initialized value. We pass the
 * deserialized value from formValues as defaultChecked so edit values persist.
 */
const AmazonKeywordFields = ({ itemCsvInput, formValues, hideNegative }) => {
  // Auto-open the section when any keyword data is already set (e.g. on edit)
  const hasKeywordData = Boolean(
    (!hideNegative && formValues?.negativeKeywords && String(formValues.negativeKeywords).trim())
    || (formValues?.customKeywords && String(formValues.customKeywords).trim())
    || (!hideNegative && formValues?.negativePhrase)
    || (!hideNegative && formValues?.negativeExact)
    || formValues?.customBroad
    || formValues?.customPhrase
    || formValues?.customExact,
  );

  return (
    <>
      {/* Line spacer above the Keyword Control section */}
      <div style={{ margin: '20px 0', height: '2px', backgroundColor: '#91BE23', width: '100%', borderRadius: '2px' }} />
      <CollapsibleSection title="Keyword Control" defaultOpen={hasKeywordData}>
        {/* Negative Keywords (hidden for Unified budget groups) */}
        {!hideNegative && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Negative Keywords{' '}
            <Tooltip
              id="negativeKeywords"
              msg="Enter negative keywords separated by commas. Line breaks are converted to commas automatically."
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="negativeKeywords"
              type="text"
              component={renderTextArea}
              format={itemCsvInput}
            />
          </div>
          <div className="form__form-group-field" style={{ marginTop: '8px', gap: '16px' }}>
            <Field
              name="negativePhrase"
              component={renderCheckBoxField}
              label="Negative Phrase"
              defaultChecked={!!formValues?.negativePhrase}
            />
            <Field
              name="negativeExact"
              component={renderCheckBoxField}
              label="Negative Exact"
              defaultChecked={!!formValues?.negativeExact}
            />
          </div>
        </div>
        )}

        {/* Custom Keywords */}
        <div className="form__form-group">
          <span className="form__form-group-label">
            Custom Keywords{' '}
            <Tooltip
              id="customKeywords"
              msg="Enter custom keywords separated by commas. Line breaks are converted to commas automatically."
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="customKeywords"
              type="text"
              component={renderTextArea}
              format={itemCsvInput}
            />
          </div>
          <div className="form__form-group-field" style={{ marginTop: '8px', gap: '16px' }}>
            <Field
              name="customBroad"
              component={renderCheckBoxField}
              label="Broad"
              defaultChecked={!!formValues?.customBroad}
            />
            <Field
              name="customPhrase"
              component={renderCheckBoxField}
              label="Phrase"
              defaultChecked={!!formValues?.customPhrase}
            />
            <Field
              name="customExact"
              component={renderCheckBoxField}
              label="Exact"
              defaultChecked={!!formValues?.customExact}
            />
          </div>
        </div>
      </CollapsibleSection>
    </>
  );
};

AmazonKeywordFields.propTypes = {
  itemCsvInput: PropTypes.func.isRequired,
  formValues: PropTypes.shape({
    negativePhrase: PropTypes.bool,
    negativeExact: PropTypes.bool,
    customBroad: PropTypes.bool,
    customPhrase: PropTypes.bool,
    customExact: PropTypes.bool,
  }),
  hideNegative: PropTypes.bool,
};

AmazonKeywordFields.defaultProps = {
  formValues: {},
  hideNegative: false,
};

export default AmazonKeywordFields;
